import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Dropdown from "react-bootstrap/Dropdown";
import DOMPurify from "dompurify";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useSelector } from "react-redux";

import {
  fetchAssetsForContentScoreCard,
  fetchContentGraphValues,
  fetchContentScore,
  invalidateContentMapAssets,
} from "src/redux/actions/contentMap";
import { getContentHealthScoreCard, getGraphValues, getSectionType } from "./contentScore.helper";
import ScoreMeter from "../components/scoreMeter";
import { ScoreCard } from "./contentScore.helper";

import styles from "./styles.module.scss";
import cx from "classnames";
import _ from "lodash";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";

interface ContentScoreProps {
  selectedCard: ScoreCard | null;
  selectedFilter: string;
  // eslint-disable-next-line no-unused-vars
  setSelectedCard: (item: ScoreCard | null) => void;
  // eslint-disable-next-line no-unused-vars
  setSelectedFilter: (value: string) => void;
}

const ContentScore: React.FC<ContentScoreProps> = ({
  selectedCard,
  selectedFilter,
  setSelectedCard,
  setSelectedFilter
}) => {
  const graphData = useSelector((state: any) =>
    _.get(state.contentMap, "contentScore.graphData.data", []),
  );
  const loading = useSelector((state: any) =>
    _.get(state.contentMap, "contentScore.graphData.loading", false),
  );
  const scoreData = useSelector((state: any) =>
    _.get(state.contentMap, "contentScore.scoreData.data", {}),
  );
  const customFields = useSelector((state: any) =>
    _.get(state.customFields, "items", []),
  );
  const isAssetsLoading = useSelector((state: any) =>
    _.get(state.contentMap, "assets.assetsList.loading", false),
  );

  const { trackContentSectionClicked, trackContentFiltered }  = useContentMapAdoption();

  const [scoreValue, setScoreValue] = React.useState(0);
  const [animatedValue, setAnimatedValue] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState(
    selectedCard?.filters || [],
  );
  const [showDetails, setShowDetails] = useState(false);

  const [data, setData] = useState([
    { name: "6m - 9m", value: 0 },
    { name: "9m - 12m", value: 0 },
    { name: "1y - 1.5y", value: 0 },
    { name: "1.5+ y", value: 0 },
  ]);

  const dispatch = useDispatch();

  const CONTENT_HEALTH_SCORE_CARD = getContentHealthScoreCard(scoreData);

  useEffect(() => {
    setScoreValue(_.get(scoreData, "averageContentScore", 0));
  }, [scoreData]);

  useEffect(() => {
    if (selectedCard) {
      const graphValues = getGraphValues(
        graphData,
        selectedCard,
        selectedFilter,
      );
      setData(graphValues);
    }
  }, [graphData]);

  useEffect(() => {
    setAnimatedValue(0);

    const animationDuration = 2000;
    const steps = 60;
    const stepDuration = animationDuration / steps;
    const increment = scoreValue / steps;

    let step = 0;
    const timer = setInterval(() => {
      step++;
      const newValue = Math.min(increment * step, scoreValue);
      setAnimatedValue(newValue);

      if (step >= steps) {
        clearInterval(timer);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [scoreValue]);

  const handleClick = (item: ScoreCard) => {

    if(isAssetsLoading){
      return;
    }

    setSelectedCard(item);
    setData([]);
    dispatch(fetchContentScore());
    setSelectedFilter(item.value);
    if(item.key !== "UNSTRUCTURED_CONTENT" && showDetails) {
      setShowDetails(false);
    }
    if(item.key === "UNSTRUCTURED_CONTENT" && !showDetails && _.size(customFields) === 0) {
      setShowDetails(true);
    }
    if (item.key === "CONTENT_EXPIRY") {
      const selectedExpiryFilter = item.filters.find(
        (filter: any) => filter.value === item.value,
      );
      if (selectedExpiryFilter && selectedExpiryFilter.subFilters) {
        setSelectedFilters(selectedExpiryFilter.subFilters);
      }
    } else {
      setSelectedFilters(item.filters || []);
    }

    dispatch(
      fetchAssetsForContentScoreCard({ page: 0, category: item.key, value: item.value }),
    );

    dispatch(
      fetchContentGraphValues({
        category: item.key,
        value: item.value,
      }),
    );
    
    trackContentSectionClicked({sectionType: getSectionType(item.key)});
    trackContentFiltered({sectionType: getSectionType(item.key), filterName: item.value });
  };

  const selectFilter = (value: string) => {
    setSelectedFilter(value);
    if (selectedCard?.key === "CONTENT_EXPIRY") {
      const selectedExpiryFilter = selectedCard.filters.find(
        (filter: any) => filter.value === value,
      );
      if (selectedExpiryFilter && selectedExpiryFilter.subFilters) {
        setSelectedFilters(selectedExpiryFilter.subFilters);
          dispatch(
          fetchContentGraphValues({
            category: selectedCard.key,
            value: value,
          }),
    );
      }
    } else {
      setSelectedFilters(selectedCard?.filters || []);
    }
    
     dispatch(
      fetchAssetsForContentScoreCard({
        page: 0,
        category: selectedCard?.key ?? "",
        value: value,
      }),
    );

    trackContentFiltered({sectionType: getSectionType(selectedCard?.key || ""), filterName: value });
  };

  const handleCloseFilter = () => {
    setSelectedCard(null);
    setShowDetails(false);
    dispatch(invalidateContentMapAssets());
  };

  return (
    <div
      className={cx(styles.container, {
        [styles.expandContainer]: selectedCard,
        [styles.showDetails]: showDetails
      })}
    >
      <div className={styles.header}>
        <div className={styles.titleContainer}>
          <div
            className={cx(styles.score, {
              [styles.hide]: selectedCard,
            })}
          >
            <div className={styles.scoreContainer}>
              <ScoreMeter animatedValue={animatedValue} />
            </div>
          </div>

          <p
            className={cx(styles.title, {
              [styles.hide]: selectedCard,
            })}
          >
            {scoreValue === 100 ? "Your content hub is in ideal shape - organized, current, and packed with sales-ready assets that are ready to drive results." : 
            <>Here&apos;s what&apos;s affecting your content health score. <br /> Fix these assets to help your sales team find better content faster.</>}
          </p>
        </div>
      </div>

      <div
        className={cx(styles.filterHeader, {
          [styles.hideFilterHeader]: !selectedCard,
        })}
      >
        <p className={styles.filterHeaderTitle}>
          {selectedCard?.title || " "}
        </p>
        <p className={styles.filterHeaderDescription}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(selectedCard?.description || ""),
        }}
        >
        </p>
        {showDetails && (
          <p
            className={styles.note}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(selectedCard?.note || "", { ADD_ATTR: ['target', 'rel'] }),
            }}
          />
        )}

        <Dropdown drop="down">
        <Dropdown.Toggle
          as="div"
          className={cx(styles.dropdown, {
            [styles.hideDropdown]:
              !selectedCard || selectedCard.key === "CONTENT_DUPLICATION",
            [styles.disableDropdown]: isAssetsLoading,
          })}
        >
          {selectedCard ? 
          selectedFilters?.find(
            (item) => item.value === selectedFilter,
          )?.name || "Select Filter"
          : "Select Filter"
          }
          <i className="paperflite-dropdown" />
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {selectedFilters &&
            selectedFilters.map((item, index) => {
              const isActive = selectedFilter === item.value;
              return (
                <Dropdown.Item
                  key={index}
                  onClick={() => {
                    selectFilter(item.value);
                  }}
                >
                  <i
                    className={cx(
                      styles.selectIcon,
                      styles.dropdownItem,
                      {
                        [styles.activeSelect]: isActive,
                      },
                      isActive
                        ? "paperflite-radio-button-selected"
                        : "paperflite-radio-button-unselected",
                    )}
                  />
                  <span className={styles.name}>{item.name}</span>
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
      </div>

      <div
        className={cx(styles.cards, {
          [styles.selectedCards]: selectedCard,
        })}
      >
        {CONTENT_HEALTH_SCORE_CARD.filter((item) => (item.count > 0) && (item?.key !== "CONTENT_DUPLICATION")).map((item, index) => (
          <ContentScoreCard
            key={index}
            item={item}
            handleClick={handleClick}
            selected={selectedCard}
            isAssetsLoading={isAssetsLoading}
          />
        ))}
      </div>

      <div
        className={cx(styles.graphContainer, {
          [styles.hideGraphContainer]:
            !selectedCard || selectedCard.key === "CONTENT_DUPLICATION",
        })}
      >
        <ContentBarGraph
          data={data}
          selectedCard={selectedCard}
          selectedFilter={selectedFilter}
          loading={loading}
          selectFilter={selectFilter}
        />
      </div>

      <ContentPieGraph data={data} selectedCard={selectedCard} />

      {selectedCard && (
        <div
          className={styles.closeFilter}
          onClick={() => {
            handleCloseFilter();
          }}
        >
          <i className={cx(styles.closeIcon, "paperflite-close")} />
          <span>close filter</span>
        </div>
      )}
    </div>
  );
};

export default ContentScore;

interface ContentScoreCardProps {
  item: {
    key: string;
    title: string;
    count: number;
    countLabel: string;
    description: string;
    icon: string;
    value: string;
  };
  // eslint-disable-next-line no-unused-vars
  handleClick: (item: ScoreCard) => void;
  selected: ScoreCard | null;
  isAssetsLoading: boolean;
}

const ContentScoreCard: React.FC<ContentScoreCardProps> = ({
  item,
  handleClick,
  selected,
  isAssetsLoading
}) => {
  return (
    <div
      className={cx(styles.card, {
        [styles.selectedCard]: item.key === selected?.key,
        [styles.disableCard]: isAssetsLoading && item.key !== selected?.key,
      })}
      onClick={() => {
        handleClick(item as ScoreCard);
      }}
    >
      <div className={styles.cardHeader}>
        <div className={styles.count}>{item.count}</div>
        <div className={styles.countLabel}>{item.countLabel}</div>
      </div>
      <i className={cx(styles.icon, item.icon)} />
      <div className={styles.title}>{item.title}</div>

      <i className={cx(styles.arrowIcon, "paperflite-arrow-up", {
        [styles.hideArrow]: isAssetsLoading
      })} />
    </div>
  );
};

interface ContentBarGraphProps {
  data: { name: string; value: number }[];
  loading: boolean;
  selectedCard: ScoreCard | null;
  selectedFilter: string;
  // eslint-disable-next-line no-unused-vars
  selectFilter: (value: string) => void;
}

const ContentBarGraph: React.FC<ContentBarGraphProps> = ({
  data,
  selectedCard,
  selectedFilter,
  loading,
  selectFilter,
}) => {

  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor", "#e51058"),
  );

  const getActiveFilter = (name: string) => {
    switch(name){
      case "ALL_EXPIRED":
      case "PAST_MONTH":
      case "ONE_TO_THREE_MONTHS":
      case "THREE_TO_SIX_MONTHS":
      case "MORE_THAN_SIX_MONTHS":
        return "ALL_EXPIRED";
      
      case "ALL_EXPIRING":
      case "EXPIRING_TODAY":
      case "EXPIRING_THIS_WEEK":
      case "EXPIRING_THIS_MONTH":
      case "EXPIRING_LATER":
        return "ALL_EXPIRING";
      
      default:
        return false;
    }
  };

  const getLabelText = () => {
    if (selectedCard?.key === "CONTENT_AGING") {
      return "content age";
    } else if (selectedCard?.key === "UNSTRUCTURED_CONTENT") {
      return "category";
    } else if (selectedFilter === "ALL_EXPIRED") {
      return "time since expiry";
    } else if (selectedFilter === "ALL_EXPIRING") {
      return "time until expiry";
    }
    return "";
  };

  const activeFilter = getActiveFilter(selectedFilter);

  return (
    <>
      <ResponsiveContainer width="100%" height={220}>
      <BarChart 
        data={data}
        margin={{ top: 10, right: 0, left: 30, bottom: 20 }}
      >
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke="#DBDBDB" 
          horizontal={true}
          vertical={false}
        />
        <XAxis
          dataKey="name"
          axisLine={false}
          tickLine={false}
          tick={({ x, y, payload }) => (
            <text
              x={x}
              y={y + 4}
              textAnchor="middle"
              fill="#A9A9A9"
              fontWeight="600"
              fontSize={11}
              dominantBaseline="hanging"
            >
              {payload.value}
            </text>
          )}
          height={50}
          label={{ 
            value: getLabelText(),
            position: 'insideBottom', 
            offset: -5,
            style: { 
              textAnchor: 'middle',
              fontSize: '12px',
              fill: '#060606',
              fontWeight: '500'
            }
          }}
        />
        <YAxis
          width={50}
          tick={({ x, y, payload }) => (
            <text
              x={x - 8}
              y={y}
              dominantBaseline="central"
              textAnchor="end"
              fill="#A9A9A9"
              fontWeight="600"
              fontSize={11}
            >
              {payload.value}
            </text>
          )}
          axisLine={false}
          tickLine={false}
          label={{
            value: 'no. of assets',
            angle: -90,
            position: 'insideLeft',
            offset: -20,
            style: {
              textAnchor: 'middle',
              fontSize: '12px',
              fill: '#060606',
              fontWeight: '500',
            }
          }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #DBDBDB',
            borderRadius: '8px',
            fontSize: '12px',
            paddingTop: '4px',
            paddingBottom: '4px',
            paddingLeft: '8px',
            paddingRight: '8px',
            boxShadow: 'none'
          }}
          labelFormatter={() => ''}
          formatter={(value, name, props) => [
            <span>
              <span style={{ color: '#A9A9A9', fontWeight: 500 }}>Assets</span>
              <span style={{ color: '#060606', fontWeight: 500 }}>{` : ${value}`}</span>
            </span>
          ]}
          labelStyle={{
            color: '#333',
            fontWeight: '600',
          }}
          itemStyle={{
            color: '#060606',
            backgroundColor: 'transparent',
          }}
          cursor={{fill: 'transparent'}}
        />
        <Bar
          dataKey="value"
          fill={primaryColor || "#e51058"}
          stroke="#1d4ed8"
          strokeWidth={1}
          radius={[4, 4, 0, 0]}
          maxBarSize={10}
        />
      </BarChart>
    </ResponsiveContainer>

      <span className={cx(styles.loading, {
        [styles.hideLoading]: !loading,
      })}>loading...</span>

      {selectedCard?.key === "CONTENT_EXPIRY" && (
        <div className={styles.expiryOptions}>
          <div
            className={cx(styles.expiryOption, {
              [styles.selectedExpiryOption]: activeFilter === "ALL_EXPIRED"
            })}
            onClick={() => {
              selectFilter("ALL_EXPIRED");
            }}
          >
            <i
              className={cx({
                "paperflite-radio-button-unselected":
                  activeFilter !== "ALL_EXPIRED",
                "paperflite-radio-button-selected":
                  activeFilter === "ALL_EXPIRED",
              })}
            />
            <span>Expired</span>
          </div>
          <div
            className={cx(styles.expiryOption, {
              [styles.selectedExpiryOption]:
                activeFilter === "ALL_EXPIRING"
            })}
            onClick={() => {
              selectFilter("ALL_EXPIRING");
            }}
          >
            <i
              className={cx({
                "paperflite-radio-button-unselected":
                  activeFilter !== "ALL_EXPIRING",
                "paperflite-radio-button-selected":
                  activeFilter === "ALL_EXPIRING",
              })}
            />
            <span>Expiring</span>
          </div>
        </div>
      )}
    </>
  );
};

interface ContentPieGraphProps {
  data: { name: string; value: number }[];
  selectedCard: ScoreCard | null;
}

const ContentPieGraph: React.FC<ContentPieGraphProps> = ({
  data,
  selectedCard,
}) => {

  const primaryColor = useSelector((state: any) =>
    _.get(state.accounts, "item.theme.primaryColor", "#e51058"),
  );

  const renderLabelLine = ({ cx, cy, midAngle, outerRadius }) => {
    const RADIAN = Math.PI / 180;
    const lineStartRadius = outerRadius + 4;
    const lineEndRadius = outerRadius + 20;
    const sx = cx + lineStartRadius * Math.cos(-midAngle * RADIAN);
    const sy = cy + lineStartRadius * Math.sin(-midAngle * RADIAN);
    const ex = cx + lineEndRadius * Math.cos(-midAngle * RADIAN);
    const ey = cy + lineEndRadius * Math.sin(-midAngle * RADIAN);

    return (
      <>
      <path d={`M${sx},${sy}L${ex},${ey}`} stroke={primaryColor || '#e51058'} fill="none" />
      </>
    );
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    outerRadius,
    name,
    value,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
      x={x}
      y={y}
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={11}
      fontWeight={600}
      >
      <tspan fill="#060606" dx={x > cx ? 8 : -8}>{name.toLowerCase()}</tspan>
      <tspan fill="#717274" dx={4}>{`: ${value}`}</tspan>
      </text>
    );
  };

  const getColor = (entry) => {
    if (entry.name === "Duplicate Assets") {
      return primaryColor || '#e51058';
    }
    return primaryColor ? `${primaryColor}80` : 'rgba(229, 16, 88, 0.5)';
  }

  return (
    <PieChart
      width={500}
      height={300}
      className={cx(styles.pieChartContainer, {
        [styles.hideGraphContainer]:
          !selectedCard || selectedCard.key !== "CONTENT_DUPLICATION",
      })}
    >
      <Pie
        data={data}
        innerRadius={60}
        outerRadius={80}
        fill="#f06292"
        paddingAngle={4}
        dataKey="value"
        stroke="none"
        labelLine={renderLabelLine}
        label={renderCustomizedLabel}
        isAnimationActive={true}
        animationBegin={100}
        animationDuration={1800}
      >
        {data.map((entry, index) => (
          <Cell
            key={`cell-${index}`}
            fill={getColor(entry)}
          />
        ))}
      </Pie>
    </PieChart>
  );
};